/**
 * 채팅 이력 영속(U2) — 새로고침해도 대화가 남게 한다.
 *
 * 백엔드는 이 본문을 **해석하지 않는다**(불투명 blob). 도메인 상태의 단일 진실 공급원은
 * 체크포인트이고, 여기 저장되는 것은 **표현 로그**일 뿐이다 — 그래서 프런트 표현이 바뀌면
 * 이 형식도 같이 바뀌어도 된다(백엔드 계약이 아니다).
 *
 * ⚠ 그 대신 **복원이 실패해도 대화는 계속돼야 한다**: 형식이 안 맞으면 조용히 빈 이력으로
 * 시작한다(막다른 종료 금지). 남은 것을 못 읽는 것보다 앞으로 못 말하는 게 나쁘다.
 */
import { request } from '@/api/client'
import { API } from '@/api/paths'
import type { ChatMessage } from './events'

interface TranscriptBody {
  messages: Record<string, unknown>[]
}

/** 사이드바 목록에 쓸 제목 상한. 백엔드 컬럼(120)보다 짧게 — 한 줄에 들어갈 만큼만. */
const TITLE_MAX = 60

/**
 * 대화 제목 — **첫 사용자 발화**에서 만든다.
 *
 * 이 계산이 프런트에 있는 이유: 제목의 재료는 transcript 인데 백엔드는 그 배열을 불투명 blob
 * 으로 보관한다(형식을 아는 쪽은 여기다). 그래서 제목은 프런트가 지어 PUT 으로 함께 보내고,
 * 백엔드는 문자열로 보관만 한다.
 *
 * 지어내지 않는다 — 사용자 발화가 없으면 `null` 을 주고, 목록은 갱신 시각으로 대신 표시한다.
 */
export function deriveTitle(messages: readonly ChatMessage[]): string | null {
  const first = messages.find((m) => m.role === 'user')
  if (!first) return null
  // 줄바꿈·연속 공백을 접어 한 줄로. 붙여넣은 장문도 목록에서는 한 줄이어야 한다.
  const line = first.text.replace(/\s+/g, ' ').trim()
  if (!line) return null
  return line.length > TITLE_MAX ? `${line.slice(0, TITLE_MAX - 1)}…` : line
}

/** 저장 — 실패는 삼킨다(이력 저장이 대화를 막으면 안 된다). 성공 여부만 돌려준다. */
export async function saveTranscript(
  threadId: string,
  messages: readonly ChatMessage[],
): Promise<boolean> {
  try {
    await request(API.transcript(threadId), {
      method: 'PUT',
      body: {
        messages: messages as unknown as Record<string, unknown>[],
        // null 은 보내지 않는다 — 백엔드에서 "변경 없음"과 구분되지 않아 제목이 지워질 수 있다.
        ...(deriveTitle(messages) ? { title: deriveTitle(messages) } : {}),
      },
    })
    return true
  } catch {
    return false
  }
}

/** 복원 — 형식이 낯설면 빈 배열. 백엔드가 blob 을 해석하지 않으므로 검증은 여기 몫이다. */
export async function loadTranscript(threadId: string): Promise<ChatMessage[]> {
  try {
    const body = await request<TranscriptBody>(API.transcript(threadId))
    const restored: ChatMessage[] = []
    for (const raw of body.messages ?? []) {
      if (isChatMessage(raw)) restored.push(raw)
    }
    return restored
  } catch {
    return []
  }
}

/** 저장된 blob 이 지금의 표현 형식과 맞는가 — 맞는 것만 살린다(반쪽 렌더 방지). */
export function isChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as { role?: unknown; text?: unknown; parts?: unknown }
  if (candidate.role === 'user') return typeof candidate.text === 'string'
  if (candidate.role === 'assistant') return Array.isArray(candidate.parts)
  return false
}
